import { Controller } from "@hotwired/stimulus"

// The floor plan on a property's Home page: switch floors, pick a room to see
// what's in it, filter pins by kind, or light up everything on one breaker.
//
// Two base layers: Essentials (the default) hides minor things like outlets,
// switches and lights; Everything shows them as small dots. Anything picked by
// a room, breaker or filter is drawn full size whichever layer is on.
export default class extends Controller {
  static targets = ["floor", "floorTab", "room", "pin", "chip", "breaker", "panel"]

  declare readonly floorTargets: HTMLElement[]
  declare readonly floorTabTargets: HTMLElement[]
  declare readonly roomTargets: HTMLElement[]
  declare readonly pinTargets: HTMLElement[]
  declare readonly chipTargets: HTMLElement[]
  declare readonly breakerTarget: HTMLSelectElement
  declare readonly hasBreakerTarget: boolean
  declare readonly panelTargets: HTMLElement[]

  private mode: "essentials" | "everything" = "essentials"

  showFloor(event: Event) {
    const id = (event.currentTarget as HTMLElement).dataset.floor
    this.floorTargets.forEach((floor) => (floor.hidden = floor.dataset.floor !== id))
    this.floorTabTargets.forEach((tab) => tab.setAttribute("aria-selected", String(tab.dataset.floor === id)))
  }

  selectRoom(event: Event) {
    const id = (event.currentTarget as HTMLElement).dataset.room
    if (!id) return

    this.clearBreaker()
    this.revealFloorFor(id)
    this.showPanel(`room-${id}`)
    this.roomTargets.forEach((room) => room.classList.toggle("is-selected", room.dataset.room === id))
    this.highlight((pin) => pin.dataset.room === id)
  }

  filter(event: Event) {
    const chip = event.currentTarget as HTMLElement
    const mode = chip.dataset.mode as "essentials" | "everything" | undefined
    const category = chip.dataset.category || ""
    if (mode) this.mode = mode

    this.chipTargets.forEach((c) => c.classList.toggle("is-active", c === chip))
    this.clearBreaker()
    this.clearRooms()
    this.showPanel("intro")
    this.highlight(category ? (pin) => pin.dataset.category === category : null)
  }

  showBreaker() {
    const id = this.breakerTarget.value
    this.clearRooms()
    this.activateModeChip()
    if (!id) {
      this.reset()
      return
    }

    this.showPanel(`circuit-${id}`)
    const lit = this.pinTargets.filter((pin) => pin.dataset.circuit === id)
    if (lit[0]) this.revealFloorFor(lit[0].dataset.room || "")
    this.highlight((pin) => pin.dataset.circuit === id)
  }

  reset() {
    this.clearBreaker()
    this.clearRooms()
    this.activateModeChip()
    this.showPanel("intro")
    this.highlight(null)
  }

  // Matching pins are drawn full size; the rest dim, and minor ones that
  // don't match are hidden. With no matcher, the current base layer shows.
  private highlight(matches: ((pin: HTMLElement) => boolean) | null) {
    this.pinTargets.forEach((pin) => {
      const minor = pin.dataset.minor === "true"
      const on = matches ? matches(pin) : false
      pin.classList.toggle("is-lit", on)
      pin.classList.toggle("is-dim", Boolean(matches) && !on && !minor)
      pin.classList.toggle("is-hidden", minor && !on && (Boolean(matches) || this.mode === "essentials"))
    })
  }

  private activateModeChip() {
    this.chipTargets.forEach((chip) => chip.classList.toggle("is-active", chip.dataset.mode === this.mode))
  }

  private showPanel(name: string) {
    this.panelTargets.forEach((panel) => (panel.hidden = panel.dataset.panel !== name))
  }

  private revealFloorFor(roomId: string) {
    const room = this.roomTargets.find((r) => r.dataset.room === roomId)
    const floor = room?.closest<HTMLElement>("[data-home-explorer-target='floor']")
    if (!floor || !floor.hidden) return

    this.floorTargets.forEach((f) => (f.hidden = f !== floor))
    this.floorTabTargets.forEach((tab) => tab.setAttribute("aria-selected", String(tab.dataset.floor === floor.dataset.floor)))
  }

  private clearRooms() {
    this.roomTargets.forEach((room) => room.classList.remove("is-selected"))
  }

  private clearBreaker() {
    if (this.hasBreakerTarget) this.breakerTarget.value = ""
  }
}
